import { Component, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { SnackbarService } from '../../core/services/snackbar.service';
import { CaseEnquiryService } from '../../core/services/case-enquiry.service';
import { CaseService } from '../../core/services/case.service';
import { AuthService } from '../../core/services/auth.service';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { Search01Icon, Add01Icon, NoteEditIcon, Invoice01Icon, TaskDone01Icon, CheckmarkCircle01Icon, ViewIcon, UserIcon, Calendar03Icon, InformationCircleIcon } from '@hugeicons/core-free-icons';
import { ModalComponent } from '../../shared/components/modal/modal.component';
import { SelectComponent, SelectOption } from '../../shared/components/select/select.component';

interface LegalCase {
    id: string | number;
    caseCode?: string;
    title: string;
    clientName: string;
    attorney: string;
    caseType: string;
    hospital: string;
    lienBalance: number;
    date: string;
    stage: 'submission' | 'offer' | 'close';
    progress: number;
    offerStatus?: string;
    currentStage?: number;
    caseProgress?: number;
}

interface TabConfig {
    key: string;
    title: string;
}

@Component({
    selector: 'app-cases',
    standalone: true,
    imports: [CommonModule, CurrencyPipe, DatePipe, FormsModule, DatePickerComponent, EmptyStateComponent, HugeiconsIconComponent, ModalComponent, SelectComponent],
    templateUrl: './cases.component.html',
    styleUrl: './cases.component.scss'
})
export class CasesComponent implements OnInit {
    cases: LegalCase[] = [];

    // Pre-computed filtered arrays — updated on load + every filter change
    filteredSubmissions: LegalCase[] = [];
    filteredOffers: LegalCase[] = [];

    tabs: TabConfig[] = [
        { key: 'current', title: 'Current Cases' },
        { key: 'complete', title: 'Complete Cases' },
        { key: 'add', title: 'Add Case' }
    ];
    activeTab: string = 'current';

    columns = [
        {
            key: 'submission',
            title: 'Case Submission',
            noDataTitle: 'No Submissions',
            noDataDescription: 'No cases pending submission.',
            noDataIcon: NoteEditIcon
        },
        {
            key: 'offer',
            title: 'Offer Stage',
            noDataTitle: 'No Offers',
            noDataDescription: 'No active offers at this stage.',
            noDataIcon: Invoice01Icon
        }
    ];

    searchTerm: string = '';
    filterDate: string = '';
    filterStatus: string = '';
    filterAttorney: string = '';

    // Icons
    searchIcon = Search01Icon;
    addIcon = Add01Icon;
    checkIcon = CheckmarkCircle01Icon;
    viewIcon = ViewIcon;
    alertIcon = TaskDone01Icon;
    userIcon = UserIcon;
    calendarIcon = Calendar03Icon;
    infoIcon = InformationCircleIcon;

    // Modal State
    showEnquiryModal = false;
    acceptedEnquiries: any[] = [];
    selectedEnquiry: any = null;

    constructor(
        private router: Router,
        private snackbar: SnackbarService,
        private caseEnquiryService: CaseEnquiryService,
        private caseService: CaseService,
        private authService: AuthService
    ) { }

    ngOnInit() {
        this.loadCases();
    }

    loadCases() {
        this.caseService.getCaseBoard().subscribe({
            next: (data) => {
                const submissionItems = data.submission || [];
                const offerItems = data.offer_stage || [];
                const closedItems = data.case_close || [];

                // Cases with current_stage >= 2 have an offer generated, move to offer stage
                const actualSubmissions: any[] = [];
                const promotedToOffer: any[] = [];

                for (const item of submissionItems) {
                    const stage = parseInt(item.current_stage || item.case_progress, 10) || 0;
                    if (stage >= 2 || item.offer_status) {
                        promotedToOffer.push(item);
                    } else {
                        actualSubmissions.push(item);
                    }
                }

                const submissions = actualSubmissions.map((item: any) => this.mapCase(item, 'submission'));
                const offers = [...promotedToOffer, ...offerItems].map((item: any) => this.mapCase(item, 'offer'));
                const closed = closedItems.map((item: any) => this.mapCase(item, 'close'));

                this.cases = [...submissions, ...offers, ...closed];
                this.applyFilters();
            },
            error: () => {
                this.snackbar.show('Failed to load cases', 'error');
            }
        });
    }

    private mapCase(item: any, stage: 'submission' | 'offer' | 'close'): LegalCase {
        const rawDate = item.incident_date || item.created_at || item.date || '';
        const date = rawDate ? rawDate.trim().slice(0, 10) : new Date().toISOString().slice(0, 10);
        return {
            id: item.case_id || item.id,
            caseCode: item.case_code,
            title: item.title,
            clientName: item.client_name,
            attorney: item.attorney_name,
            caseType: item.type,
            hospital: item.hospital,
            lienBalance: parseFloat(item.lien_balance) || 0,
            date,
            stage: stage,
            progress: item.progress_percent || 0,
            offerStatus: item.offer_status || null,
            currentStage: parseInt(item.current_stage, 10) || 0,
            caseProgress: parseInt(item.case_progress || item.current_stage, 10) || 0
        };
    }

    get isAdmin(): boolean {
        return this.authService.getRole() === 'ADMIN';
    }

    get statusOptions(): SelectOption[] {
        const options: SelectOption[] = this.progressSteps.map(s => ({ label: s.label, value: s.label }));
        options.push({ label: 'Offer Rejected', value: 'Offer Rejected' });
        return options;
    }

    get attorneyOptions(): SelectOption[] {
        return this.attorneyList.map(name => ({ label: name, value: name }));
    }

    /** Unique attorney names from all cases */
    get attorneyList(): string[] {
        const names = new Set<string>();
        for (const c of this.cases) {
            if (c.attorney) names.add(c.attorney);
        }
        return Array.from(names).sort();
    }

    /** Unique YYYY-MM-DD dates from all cases — used to highlight calendar days. */
    get caseDates(): string[] {
        const seen = new Set<string>();
        for (const c of this.cases) {
            const d = (c.date ?? '').trim().slice(0, 10);
            if (d) seen.add(d);
        }
        return Array.from(seen);
    }

    get currentCases(): LegalCase[] {
        return this.cases.filter(c => c.stage === 'submission' || c.stage === 'offer');
    }

    get completeCases(): LegalCase[] {
        return this.cases.filter(c => c.stage === 'close');
    }

    get filteredCurrentCases(): LegalCase[] {
        return [...this.filterByStage('submission'), ...this.filterByStage('offer')];
    }

    get filteredCompleteCases(): LegalCase[] {
        const term = this.searchTerm.toLowerCase().trim();
        return this.completeCases.filter(c => {
            const matchesSearch = !term ||
                c.id.toString().toLowerCase().includes(term) ||
                (c.caseCode && c.caseCode.toLowerCase().includes(term)) ||
                c.clientName.toLowerCase().includes(term);
            const matchesDate = !this.filterDate || (c.date ?? '').startsWith(this.filterDate);
            return matchesSearch && matchesDate;
        });
    }

    get currentCaseCount(): number {
        return this.currentCases.length;
    }

    get completeCaseCount(): number {
        return this.completeCases.length;
    }

    changeTab(tabKey: string): void {
        if (tabKey === 'add') {
            this.navigateToAdd();
            return;
        }
        this.activeTab = tabKey;
    }

    private filterByStage(stage: string): LegalCase[] {
        const term = this.searchTerm.toLowerCase().trim();
        return this.cases.filter(c => {
            if (c.stage !== stage) return false;
            const matchesSearch = !term ||
                c.id.toString().toLowerCase().includes(term) ||
                (c.caseCode && c.caseCode.toLowerCase().includes(term)) ||
                c.clientName.toLowerCase().includes(term);
            const matchesDate = !this.filterDate || (c.date ?? '').startsWith(this.filterDate);
            const matchesStatus = !this.filterStatus || this.getProgressStatus(c)?.label === this.filterStatus;
            const matchesAttorney = !this.filterAttorney || c.attorney === this.filterAttorney;
            return matchesSearch && matchesDate && matchesStatus && matchesAttorney;
        });
    }

    /** Fires on every keystroke in the search box */
    onSearchInput(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
        this.applyFilters();
    }

    /** Called when date or status filter changes */
    onFilterChange(): void {
        this.applyFilters();
    }

    clearFilters(): void {
        this.searchTerm = '';
        this.filterDate = '';
        this.filterStatus = '';
        this.filterAttorney = '';
        this.applyFilters();
    }

    private applyFilters(): void {
        this.filteredSubmissions = this.filterByStage('submission');
        this.filteredOffers      = this.filterByStage('offer');
    }

    readonly progressSteps: { label: string; variant: string }[] = [
        { label: 'Submitted', variant: 'warning' },
        { label: 'Offer Generated', variant: 'orange' },
        { label: 'Offer Status', variant: 'info' },
        { label: 'Patient Payment', variant: 'purple' },
        { label: 'Hospital Payment', variant: 'teal' },
        { label: 'Release of Lien', variant: 'cyan' },
        { label: 'Pending Trust Funds', variant: 'success' }
    ];

    getProgressStatus(item: LegalCase): { label: string; variant: string } | null {
        const progress = item.caseProgress || 0;

        if (item.stage === 'submission') {
            return this.progressSteps[0];
        }

        if (item.stage === 'offer') {
            if (item.offerStatus === 'REJECTED') {
                return { label: 'Offer Rejected', variant: 'error' };
            }

            if (progress >= 7) {
                return this.progressSteps[6];
            }

            // case_progress = last completed step, so current active step = progress + 1
            // Array is 0-indexed, so active step index = (progress + 1) - 1 = progress
            const activeIndex = Math.max(0, Math.min(progress, 6));
            return this.progressSteps[activeIndex];
        }

        return null;
    }

    viewCaseDetails(item: LegalCase) {
        this.router.navigate(['/cases', item.id]);
    }

    navigateToAdd() {
        this.caseEnquiryService.getAcceptedEnquiries().subscribe({
            next: (data) => {
                this.acceptedEnquiries = data.map(e => ({
                    ...e,
                    displayId: e.case_enquiry_id || e.id,
                    displayDate: e.incident_date || e.created_at || new Date().toISOString().split('T')[0]
                }));
                this.showEnquiryModal = true;
                this.selectedEnquiry = null; // Reset selection
            },
            error: () => {
                this.snackbar.showError('Failed to load accepted inquiries');
            }
        });
    }

    selectEnquiry(enquiry: any) {
        this.selectedEnquiry = enquiry;
    }

    proceedWithCase() {
        if (this.selectedEnquiry) {
            const enquiryId = this.selectedEnquiry.displayId || this.selectedEnquiry.case_enquiry_id || this.selectedEnquiry.id;
            this.router.navigate(['/cases/add-details'], { queryParams: { enquiryId: enquiryId } });
            this.closeModal();
        } else {
            this.snackbar.showError('Please select a case inquiry to proceed');
        }
    }

    closeModal() {
        this.showEnquiryModal = false;
        this.acceptedEnquiries = [];
        this.selectedEnquiry = null;
    }

}
